import React from 'react';
import idx from 'idx';
import styled from 'styled-components';

import { GROUP_IDS } from '../../constants';
import { isAdmin, isCoordinator, isMemberOfGroup } from 'helpers';

import SidebarItem from './SidebarItem';
import SubMenuContainer from './SubMenuContainer';

import searchIcon from 'images/objects/search.svg';
import searchIconActive from 'images/objects/search_active.svg';

import homeIcon from 'images/objects/home.svg';
import homeIconActive from 'images/objects/home_active.svg';

import eCommerceIcon from 'images/objects/e-commerce.svg';
import eCommerceIconActive from 'images/objects/e-commerce_active.svg';

import crIcon from 'images/objects/cr.svg';
import crIconActive from 'images/objects/cr_active.svg';

import softwareIcon from 'images/objects/software.svg';
import softwareIconActive from 'images/objects/software_active.svg';

import adminIcon from 'images/objects/admin.svg';
import adminIconActive from 'images/objects/admin_active.svg';

import afterSalesIcon from 'images/objects/after_sales.svg';
import afterSalesIconActive from 'images/objects/after_sales_active.svg';

import corporateInformationIcon from 'images/objects/corporate_information.svg';
import corporateInformationIconActive from 'images/objects/corporate_information_active.svg';

import productInfoIcon from 'images/objects/product_information.svg';
import productInfoIconActive from 'images/objects/product_information_active.svg';

import qsrIcon from 'images/objects/qsr.svg';
import qsrIconActive from 'images/objects/qsr_active.svg';

import salesSupportIcon from 'images/objects/sales_support.svg';
import salesSupportIconActive from 'images/objects/sales_support_active.svg';

import spreaderManualsIcon from 'images/objects/spreader_manuals.svg';
import spreaderManualsIconActive from 'images/objects/spreader_manuals_active.svg';

import { colors } from 'components/variables';

const Menu = styled.nav`
	background: ${colors.background.secondary};
`;

const List = styled.ul`
	margin: 0;
	padding: 0;
`;

const Divider = styled.div`
	height: 1px;
	background: ${colors.grey.normal};
	margin: 16px 16px 16px 42px;
`;
class SidebarMenu extends React.Component {
	state = {
		expanded: null,
	};

	setExpanded = key => {
		this.setState(state => ({ expanded: state.expanded === key ? null : key }));
	};

	toggleSidebar = () => {
		this.setState({
			expanded: null,
		});

		this.props.toggleSidebar();
	};

	render() {
		const auth = idx(this.props, _ => _.auth);

		if (!auth) return null;

		const canWrite = type => idx(auth, _ => _.can.write.types[type]);
		const canRead = type => idx(auth, _ => _.can.read.types[type]);

		// const eComToken = idx(auth, _ => _.eComToken);

		// Admin if you can write user, page, group or admin notifications
		const canAdmin =
			canWrite('user') ||
			canWrite('page') ||
			canWrite('group') ||
			canWrite('admin_notification');

		// See special sw requires read for folder and any of admin, coordinator and special sw group
		const canSpecialSW =
			canRead('folder') &&
			(isAdmin(auth) ||
				isCoordinator(auth) ||
				isMemberOfGroup(auth, GROUP_IDS.specialSoftware));

		// See if user can see bromma select (in sales support)
		const canBrommaSelect =
			isAdmin(auth) ||
			isCoordinator(auth) ||
			isMemberOfGroup(auth, GROUP_IDS.brommaSpreaderDivision);

		const canEcommerce = isAdmin(auth) || isMemberOfGroup(auth, 26);
		const canBrommaPartsInternal =
			isAdmin(auth) || !isMemberOfGroup(auth, GROUP_IDS.customer);

		const canCorporateItems = canRead('page');

		const canProductInformationItems = canRead('product_information');
		const productInformationItems = [
			{
				name: 'PI-sheets',
				to: '/product-information/pi-sheets',
				canAccess: true,
			},
		];

		const canAfterSalesItems =
			canRead('bulletin') || canRead('folder') || canRead('page');
		const afterSalesItems = [
			{
				name: 'RUP',
				to: '/browse/after-sales/50000',
				canAccess: canRead('folder'),
			},
			{
				name: 'Spare parts',
				to: '/browse/after-sales/60000',
				canAccess:
					isMemberOfGroup(auth, GROUP_IDS.brommaSpreaderDivision) ||
					isAdmin(auth),
			},
			{
				name: 'Service Bulletins',
				to: '/after-sales/service-bulletins',
				canAccess: canRead('bulletin'),
			},
			{
				name: 'Projects',
				to: '/browse/after-sales/5',
				canAccess: canRead('folder'),
			},
			{
				name: 'Service Handbook',
				to: '/after-sales/14',
				canAccess: canRead('page'),
			},
		];

		const canSalesSupportItems = canRead('page') || canBrommaSelect;
		const salesSupportItems = [
			{
				name: 'Bromma Sales Handbook',
				to: 'https://drive.google.com/drive/folders/0AHNoEZUU3rtOUk9PVA',
				external: true,
				canAccess:
					isMemberOfGroup(auth, GROUP_IDS.brommaSpreaderDivision) ||
					isAdmin(auth),
			},
			{
				name: 'Terms and Conditions',
				to: '/browse/software/6',
				canAccess: canRead('folder'),
			},
			{
				name: 'ISO Certificates',
				to: '/sales-support/9',
				canAccess: canRead('page'),
			},
			{
				name: 'Sales Support Agents',
				to: '/sales-support/10',
				canAccess: canRead('page'),
			},
			{
				name: 'Bromma Select',
				to: '/sales-support/11',
				canAccess: canBrommaSelect,
			},
		];

		const canSoftwareItems =
			canRead('folder') || canSpecialSW || canRead('sourcesafe');
		const softwareItems = [
			{
				name: 'Official Software',
				to: '/browse/software/1',
				canAccess: canRead('folder'),
			},
			{
				name: 'Special Software',
				to: '/browse/software/8',
				canAccess: canSpecialSW,
			},
			{
				name: 'Bromma Sourcesafe',
				to: '/sourcesafe',
				canAccess: canRead('sourcesafe'),
			},
		];

		const canSpreaderManualItems =
			canRead('folder') || canRead('spreader_document');
		const spreaderManualItems = [
			{
				name: 'Spreader manuals',
				to: '/browse/spreader-manuals/7',
				canAccess: true,
			},
			{
				name: 'Search',
				to: '/spreader-manuals/search',
				canAccess: true,
			},
		];

		const canQsrItems =
			canRead('qsr') || isMemberOfGroup(auth, GROUP_IDS.externalReader);
		const qsrItems = [
			{
				name: 'Overview',
				to: '/qsrs/overview',
				canAccess: true,
			},
			{
				name: 'Create report',
				to: '/qsrs/add',
				canAccess: true,
			},
			{
				name: 'Reports',
				to: '/qsrs',
				exact: true,
				canAccess: true,
			},
			{
				name: 'Search reports',
				to: '/qsrs/search',
				canAccess: true,
			},
			{
				name: 'QSR assistant',
				to: 'https://qsrbot.bromma.com/',
				canAccess: true,
				external: true,
			},
		];

		const crItems = [
			{
				name: 'Create report',
				to: '/crs/add',
				canAccess: true,
			},
			{
				name: 'Reports',
				to: '/crs',
				exact: true,
				canAccess: true,
			},
			{
				name: 'Request form',
				to: '/crs/commissioning-request-form',
				canAccess: true,
			},
		];

		const canGiveAwayShop =
			isAdmin(auth) ||
			isMemberOfGroup(auth, GROUP_IDS.brommaSpreaderDivision) ||
			isMemberOfGroup(auth, GROUP_IDS.frontline);

		const canWorkWear =
			isAdmin(auth) ||
			isMemberOfGroup(auth, GROUP_IDS.brommaSpreaderDivision) ||
			isMemberOfGroup(auth, GROUP_IDS.frontline);

		return (
			<Menu>
				<List>
					<SidebarItem
						exact
						to="/search"
						name="Search"
						icon={searchIcon}
						iconActive={searchIconActive}
						onClick={this.toggleSidebar}
						hideOnLarge
					/>

					<SidebarItem
						exact
						to="/"
						name="Home"
						icon={homeIcon}
						iconActive={homeIconActive}
						onClick={this.toggleSidebar}
					/>

					{canCorporateItems && (
						<SidebarItem
							external
							to="https://bromma-mediaportal.qbank.se/"
							name="Bromma Brand Bank"
							icon={corporateInformationIcon}
							iconActive={corporateInformationIconActive}
							onClick={this.toggleSidebar}
						/>
					)}

					{canProductInformationItems && (
						<SubMenuContainer
							name="Product information"
							to="/product-information"
							icon={productInfoIcon}
							iconActive={productInfoIconActive}
							expanded={this.state.expanded}
							toggle={this.setExpanded}
							onClick={this.toggleSidebar}
							items={productInformationItems}
						/>
					)}

					{canAfterSalesItems && (
						<SubMenuContainer
							name="Services"
							to={['/after-sales', '/browse/after-sales']}
							icon={afterSalesIcon}
							iconActive={afterSalesIconActive}
							expanded={this.state.expanded}
							toggle={this.setExpanded}
							onClick={this.toggleSidebar}
							items={afterSalesItems}
						/>
					)}

					{canSalesSupportItems && (
						<SubMenuContainer
							name="Sales Support"
							to={['/sales-support', '/browse/software/6']}
							icon={salesSupportIcon}
							iconActive={salesSupportIconActive}
							expanded={this.state.expanded}
							toggle={this.setExpanded}
							onClick={this.toggleSidebar}
							items={salesSupportItems}
						/>
					)}

					{canSoftwareItems && (
						<SubMenuContainer
							name="Software"
							to={['/browse/software/1', '/browse/software/8', '/sourcesafe']}
							icon={softwareIcon}
							iconActive={softwareIconActive}
							expanded={this.state.expanded}
							toggle={this.setExpanded}
							onClick={this.toggleSidebar}
							items={softwareItems}
						/>
					)}

					{canSpreaderManualItems && (
						<SubMenuContainer
							name="Spreader manuals"
							to={['/browse/spreader-manuals', '/spreader-manuals']}
							icon={spreaderManualsIcon}
							iconActive={spreaderManualsIconActive}
							expanded={this.state.expanded}
							toggle={this.setExpanded}
							onClick={this.toggleSidebar}
							items={spreaderManualItems}
						/>
					)}

					{canQsrItems && (
						<SubMenuContainer
							name="QSR"
							to="/qsrs"
							icon={qsrIcon}
							iconActive={qsrIconActive}
							expanded={this.state.expanded}
							toggle={this.setExpanded}
							onClick={this.toggleSidebar}
							items={qsrItems}
						/>
					)}

					{canRead('cr') && (
						<SubMenuContainer
							name="CR"
							to="/crs"
							icon={crIcon}
							iconActive={crIconActive}
							expanded={this.state.expanded}
							toggle={this.setExpanded}
							onClick={this.toggleSidebar}
							items={crItems}
						/>
					)}
					<Divider />

					{canEcommerce && (
						<SidebarItem
							external={true}
							to={`https://parts.bromma.com`}
							name="BrommaParts"
							icon={eCommerceIcon}
							iconActive={eCommerceIconActive}
							onClick={this.toggleSidebar}
						/>
					)}

					{canBrommaPartsInternal &&
						canEcommerce && (
							<SidebarItem
								external={true}
								to={`https://parts.bromma.com/internal`}
								name="BrommaParts (Internal)"
								icon={eCommerceIcon}
								iconActive={eCommerceIconActive}
								onClick={this.toggleSidebar}
							/>
						)}

					{(canWorkWear || canGiveAwayShop) && (
						<SidebarItem
							external
							to="https://bromma.profilestore.com/"
							name="Bromma Brand Store"
							icon={eCommerceIcon}
							iconActive={eCommerceIconActive}
							onClick={this.toggleSidebar}
						/>
					)}

					{canAdmin && (
						<SidebarItem
							to="/admin"
							name="Admin"
							icon={adminIcon}
							iconActive={adminIconActive}
							onClick={this.toggleSidebar}
						/>
					)}
				</List>
			</Menu>
		);
	}
}

export default SidebarMenu;



// WEBPACK FOOTER //
// ./src/components/Sidebar/SidebarMenu.js